import React from 'react';
import { Link } from 'react-router-dom';
import { FloralSeparator, DecorativeLeaf } from '@/components/ui/FloralElements';
import { Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Logo image
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/ob2xdoj4_279749100_449900870272747_652948079724847386_n.jpg';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary relative overflow-hidden">
      {/* Floral separator */}
      <FloralSeparator />

      <div className="container mx-auto px-4 md:px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src={LOGO_URL} 
                alt="Little House on the Table" 
                className="h-12 w-12 rounded-full object-cover shadow-md"
              />
              <div className="flex flex-col">
                <span className="font-script text-lg text-primary leading-none">
                  Little House
                </span>
                <span className="font-serif text-[10px] text-muted-foreground tracking-wider uppercase">
                  on the Table
                </span>
              </div>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-4">
              A 7th generation Nebraska family ranch, bringing you handmade, homegrown, 
              and homemade products with love and care.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/LittleHouseontheTable"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <DecorativeLeaf className="w-5 h-5" variant="sage" />
              Quick Links
            </h4>
            <ul className="space-y-2">
              {[
                { href: '/', label: 'Home' },
                { href: '/about', label: 'About Us' },
                { href: '/goats', label: 'Goats & Ranch' },
                { href: '/shop', label: 'Shop' },
                { href: '/gallery', label: 'Gallery' },
                { href: '/reviews', label: 'Reviews' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <DecorativeLeaf className="w-5 h-5" variant="accent" />
              Contact Us
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Crawford, NE, United States</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <a
                  href="tel:+1-308-665-5400"
                  className="hover:text-primary transition-colors"
                >
                  (308) 665-5400
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                <a
                  href="mailto:wohlersbeef@gmail.com"
                  className="hover:text-primary transition-colors"
                >
                  wohlersbeef@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-serif text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <DecorativeLeaf className="w-5 h-5" variant="rose" />
              Newsletter
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              Subscribe for updates on new products and ranch life!
            </p>
            <form 
              action="https://littlehouseonthetable.us1.list-manage.com/subscribe/post?u=7312e6b926ab34d87f6f608fb&amp;id=9adcb06710&amp;f_id=0092c6e1f0"
              method="post"
              target="_blank"
              noValidate
              className="flex flex-col gap-2"
            >
              <Input
                type="email"
                name="EMAIL"
                id="mce-EMAIL"
                placeholder="Your email address"
                className="bg-card border-border"
                required
              />
              {/* Bot prevention field - hidden */}
              <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                <input
                  type="text"
                  name="b_7312e6b926ab34d87f6f608fb_9adcb06710"
                  tabIndex="-1"
                  defaultValue=""
                />
              </div>
              <Button type="submit" className="btn-beveled-sage w-full">
                Subscribe
              </Button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-border/50">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {currentYear} Little House on the Table. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/faq" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                FAQ
              </Link>
              <span className="text-muted-foreground/30">|</span>
              <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <span className="text-muted-foreground/30">|</span>
              <span className="text-sm text-muted-foreground">
                A Nebraska Family Ranch
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
